import { useEffect, useRef, useState } from 'react';
import api from '../../services/api.ts';

export interface AttendanceSummary {
  enabled: boolean;
  owner: { id: number; kind: string; name: string; photo_url?: string | null } | null;
  version: number;
  human_mode: boolean;
  history?: Array<{ action: string; source?: string; actor?: { id?: number | null; kind?: string; name?: string } }>;
}

type Entry = { summary: AttendanceSummary | null; loaded: boolean; pending: boolean; generation: number; listeners: Set<() => void> };
const cache = new Map<string, Entry>();
const queue: Array<() => Promise<void>> = [];
let running = 0;
let subscribers = 0;

function drain() {
  while (running < 4 && queue.length) {
    const task = queue.shift()!;
    running += 1;
    void task().finally(() => { running -= 1; drain(); });
  }
}
function invalidate() {
  cache.forEach(entry => {
    entry.loaded = false;
    entry.summary = null;
    entry.generation += 1;
    entry.listeners.forEach(listener => listener());
  });
}
function request(entry: Entry, clinicId: string, contactId: number) {
  if (entry.loaded || entry.pending) return;
  entry.pending = true;
  queue.push(async () => {
    // A row scrolled away while waiting does not need a request.
    if (!entry.listeners.size) { entry.pending = false; return; }
    const generation = entry.generation;
    try {
      const response = await api.get(`/api/contacts/${contactId}/attendance`, {
        params: { clinic_id: clinicId, history_limit: 1 },
      });
      if (entry.generation === generation) {
        entry.summary = response.data;
        entry.loaded = true;
      }
    } catch {
      // Keep the existing mode badge on transient failures; retry on invalidation/focus.
      if (entry.generation === generation) entry.loaded = true;
    } finally {
      entry.pending = false;
      entry.listeners.forEach(listener => listener());
    }
  });
  drain();
}

export function useContactAttendanceSummary(contactId: number, enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const clinicId = localStorage.getItem('clinic_id') || '';
  const key = `${clinicId}:${contactId}`;
  const [result, setResult] = useState<{ key: string; summary: AttendanceSummary | null } | null>(null);

  useEffect(() => {
    if (!enabled || !clinicId || !contactId) return;
    let entry = cache.get(key);
    if (!entry) {
      entry = { summary: null, loaded: false, pending: false, generation: 0, listeners: new Set() };
      cache.set(key, entry);
    }
    const current = entry;
    let visible = false;
    let disposed = false;
    const update = () => {
      if (disposed || !visible) return;
      setResult({ key, summary: current.summary });
      request(current, clinicId, contactId);
    };
    const setVisible = (value: boolean) => {
      visible = value;
      if (value) { current.listeners.add(update); update(); }
      else current.listeners.delete(update);
    };
    if (subscribers++ === 0) {
      window.addEventListener('contact-attendance-changed', invalidate);
      window.addEventListener('focus', invalidate);
      // Cached summaries may have changed while the chat was unmounted.
      invalidate();
    }
    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== 'undefined' && ref.current) {
      observer = new IntersectionObserver(entries => setVisible(entries.some(item => item.isIntersecting)));
      observer.observe(ref.current);
    } else setVisible(true);
    return () => {
      disposed = true;
      observer?.disconnect();
      current.listeners.delete(update);
      if (--subscribers === 0) {
        window.removeEventListener('contact-attendance-changed', invalidate);
        window.removeEventListener('focus', invalidate);
      }
    };
  }, [contactId, clinicId, key, enabled]);

  return { ref, summary: enabled && result?.key === key ? result.summary : null };
}
